import React, { useEffect, useState } from "react";
import axios from "axios";
import { Gauge, Wrench, FileCode, Flame, Phone, MapPin, ArrowRight, Star, Check } from "lucide-react";
import { Toaster } from "sonner";
import { useSiteMeta } from "./hooks/useSiteMeta";
import { useContactInfo } from "./hooks/useContactInfo";
import { Navbar, Footer } from "./App";
import { AnimationEmbed } from "./components/AnimationEmbed";


const API = process.env.REACT_APP_BACKEND_URL;

const ReprogPage = () => {
  const [businessHours, setBusinessHours] = useState([]);
  const [homeContent, setHomeContent] = useState({});
  const [reviews, setReviews] = useState({ rating: 4.8, user_ratings_total: 107, url: "" });
  const [cms, setCms] = useState({});
  const contact = useContactInfo();

  const cmsSeo = cms?.seo || {};
  useSiteMeta({
    title: cmsSeo.title || "Reprogrammation Moteur Stage 1 & 2 Clermont | ATR",
    description:
      cmsSeo.description ||
      "Spécialiste reprogrammation moteur à Clermont-Ferrand. Stage 1 +15 à 25% de puissance, Stage 2 sur mesure, Flexfuel E85. À partir de 300€ TTC — Devis gratuit.",
    canonical_path: "/reprogrammation-moteur-clermont-ferrand",
    og_image: `${window.location.origin}/og/reprogrammation.jpg`,
    og_image_alt: "Reprogrammation moteur Stage 1 & 2 — Auvergne Tech Racing, Clermont-Ferrand",
  });

  useEffect(() => {
    axios.get(`${API}/api/business-hours`).then((r) => setBusinessHours(r.data)).catch(() => {});
    axios.get(`${API}/api/content/home_landing`).then((r) => setHomeContent(r.data)).catch(() => {});
    axios.get(`${API}/api/content/reprog_page`).then((r) => setCms(r.data || {})).catch(() => {});
    axios
      .get(`${API}/api/google-reviews`)
      .then((r) =>
        setReviews({
          rating: r.data.rating ?? 4.8,
          user_ratings_total: r.data.user_ratings_total ?? 107,
          url: r.data.url || "",
        })
      )
      .catch(() => {});
  }, []);

  const iconMap = { gauge: Gauge, wrench: Wrench, flame: Flame, "file-code": FileCode };
  const defaultIcons = [Gauge, Wrench, Flame, FileCode];

  const hero = cms.hero || {};
  const cmsServices = Array.isArray(cms.services) && cms.services.length ? cms.services : null;
  const services = cmsServices
    ? cmsServices.map((s, i) => ({
        icon: iconMap[s.icon] || defaultIcons[i] || Gauge,
        title: s.title,
        description: s.description,
        price: s.price,
        link: s.link_href ? { label: s.link_label || "Voir plus", href: s.link_href } : undefined,
      }))
    : [
        { icon: Gauge, title: "Stage 1 — Optimisation moteur", description: "Reprogrammation de l'ECU d'origine pour un gain de +15 à +25% de puissance et de couple. Aucune pièce mécanique à changer. Compatible avec un usage quotidien et un contrôle technique.", price: "À partir de 300€ TTC" },
        { icon: Wrench, title: "Stage 2 — Préparation avancée", description: "Reprogrammation poussée, conçue pour accompagner des modifications mécaniques (admission, échappement, turbo). Cartographie sur mesure établie sur banc de puissance.", price: "Tarif sur devis" },
        { icon: Flame, title: "Reprogrammation Flexfuel E85", description: "Adaptez votre cartographie moteur pour rouler au bioéthanol E85. Compatible Stage 1 et Stage 2.", link: { label: "Voir notre page dédiée", href: "/conversion-flexfuel-e85" } },
        { icon: FileCode, title: "Service fichiers ECU en ligne", description: "Vous êtes professionnel ou passionné ? Envoyez votre fichier ECU, nous vous retournons la cartographie optimisée sous 24h ouvrées.", link: { label: "Envoyer un fichier", href: "/file-service" } },
      ];

  const formatAdvantage = (s) =>
    (s || "").replace(/\{rating\}/g, String(reviews.rating).replace(".", ",")).replace(/\{reviews_count\}/g, String(reviews.user_ratings_total));

  const advantages = (Array.isArray(cms.advantages) && cms.advantages.length ? cms.advantages : [
    "Matériel professionnel : outils de lecture/écriture ECU de dernière génération",
    "Cartographies testées et validées sur route",
    "{rating}/5 sur Google — plus de {reviews_count} avis clients",
    "Toutes marques : Renault, Peugeot, Volkswagen, BMW, Mercedes, Audi, Porsche et plus",
    "Garage indépendant à Clermont-Ferrand — sans intermédiaire",
  ]).map(formatAdvantage);

  const finalCta = cms.final_cta || {};

  const serviceJsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "Reprogrammation moteur",
    name: "Reprogrammation Moteur Stage 1 & Stage 2 — Auvergne Tech Racing",
    description:
      "Reprogrammation moteur (ECU) à Clermont-Ferrand. Stage 1 pour un gain de +15 à +25% de puissance, Stage 2 sur mesure et cartographies Flexfuel E85.",
    provider: {
      "@type": "AutoRepair",
      name: contact.business_name,
      telephone: contact.phone,
      address: {
        "@type": "PostalAddress",
        streetAddress: contact.street,
        addressLocality: contact.city,
        postalCode: contact.postal_code,
        addressCountry: contact.country,
      },
    },
    areaServed: { "@type": "City", name: contact.city },
    offers: {
      "@type": "Offer",
      price: "300.00",
      priceCurrency: "EUR",
      availability: "https://schema.org/InStock",
      priceValidUntil: "2027-12-31",
    },
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: reviews.rating || 4.8,
      reviewCount: reviews.user_ratings_total || 107,
      bestRating: 5,
      worstRating: 1,
    },
  };

  return (
    <div className="bg-[#0A0A0A] min-h-screen" data-testid="reprog-page">
      <script
        type="application/ld+json"
        data-testid="reprog-service-jsonld"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceJsonLd) }}
      />
      <Toaster position="top-right" richColors />
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden">
        {/* Background image (éditable via CMS) */}
        {cms.hero_bg_image_url && (
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{ backgroundImage: `url('${cms.hero_bg_image_url}')` }}
            data-testid="reprog-hero-bg"
          />
        )}
        {/* Overlay pour lisibilité du texte */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-[#0A0A0A]" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#FF3B30]/[0.08] via-transparent to-transparent" />
        <div className="max-w-5xl mx-auto px-6 relative z-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#FF3B30]/10 border border-[#FF3B30]/30 rounded-full mb-6">
            <Gauge size={14} className="text-[#FF3B30]" />
            <span className="text-[#FF3B30] text-xs font-bold uppercase tracking-wider">
              {hero.badge || "Stage 1 · Stage 2 · Flexfuel"}
            </span>
          </div>

          <h1
            className="text-4xl sm:text-5xl md:text-6xl font-black tracking-tight text-white mb-6 leading-tight"
            data-testid="reprog-h1"
          >
            {cmsSeo.h1_prefix || hero.h1_top || "Reprogrammation Moteur"}
            <br />
            <span className="text-[#FF3B30]">{cmsSeo.h1_highlight || hero.h1_highlight || "à Clermont-Ferrand"}</span>
          </h1>

          <p className="text-lg md:text-xl text-white/70 leading-relaxed mb-8 max-w-3xl">
            {hero.intro || "Chez Auvergne Tech Racing, la reprogrammation moteur est au cœur de notre métier. Nous optimisons votre calculateur moteur (ECU) pour libérer le potentiel réel de votre véhicule — plus de puissance, plus de couple, et une consommation souvent réduite. Résultat immédiat, toutes marques."}
          </p>

          <div className="flex flex-wrap gap-3 items-center mb-8">
            <a
              href={`tel:${contact.phone}`}
              data-testid="reprog-cta-call"
              className="inline-flex items-center gap-2 bg-[#FF3B30] hover:bg-[#D63025] text-white font-bold px-6 py-4 rounded-lg transition-colors uppercase tracking-wide text-sm"
            >
              <Phone size={16} />
              {hero.cta_phone_label || "06 49 07 08 20"}
            </a>
            <a
              href="/#booking"
              data-testid="reprog-cta-book"
              className="inline-flex items-center gap-2 bg-white/[0.06] hover:bg-white/[0.12] text-white font-semibold border border-white/20 px-6 py-4 rounded-lg transition-colors uppercase tracking-wide text-sm"
            >
              {hero.cta_book_label || "Devis gratuit"}
              <ArrowRight size={16} />
            </a>
          </div>

          <div className="flex items-center gap-2 text-white/60 text-sm" data-testid="reprog-reviews-badge">
            <div className="flex text-yellow-400">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={14} className="fill-yellow-400" />
              ))}
            </div>
            <span>
              {String(reviews.rating).replace(".", ",")}/5 — {reviews.user_ratings_total} avis Google
            </span>
          </div>
        </div>
      </section>

      {/* Animation Reprogrammation */}
      <section className="py-12 md:py-16 bg-[#0A0A0A] border-t border-white/[0.06]" data-testid="reprog-page-animation-section">
        <div className="max-w-5xl mx-auto px-6">
          <AnimationEmbed src="/reprogrammation.html" title="Reprogrammation Moteur" testId="reprog-animation" />
        </div>
      </section>

      {/* Services / prestations */}
      <section className="py-16 md:py-24 bg-[#121212] border-t border-white/[0.06]">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 tracking-tight text-center">
            {cms.services_title_before || "Nos prestations"} <span className="text-[#FF3B30]">{cms.services_title_highlight || "reprogrammation"}</span>
          </h2>
          <p className="text-white/60 text-center max-w-2xl mx-auto mb-12">
            {cms.services_subtitle || "Un service adapté à votre véhicule et à votre usage — quotidien, sportif ou compétition."}
          </p>

          <div className="grid md:grid-cols-2 gap-6">
            {services.map((s, i) => {
              const Icon = s.icon;
              return (
                <article
                  key={i}
                  data-testid={`reprog-service-${i}`}
                  className="bg-black/40 border border-white/[0.06] rounded-xl p-6 hover:border-[#FF3B30]/30 transition-colors flex flex-col"
                >
                  <Icon className="text-[#FF3B30] mb-4" size={32} />
                  <h3 className="text-white font-bold text-xl mb-3">{s.title}</h3>
                  <p className="text-white/60 text-sm leading-relaxed flex-1">{s.description}</p>
                  {s.price && (
                    <div className="mt-4 pt-4 border-t border-white/[0.06]">
                      <span className="text-[#FF3B30] font-bold">{s.price}</span>
                    </div>
                  )}
                  {s.link && (
                    <a
                      href={s.link.href}
                      className="mt-4 pt-4 border-t border-white/[0.06] inline-flex items-center gap-2 text-[#FF3B30] hover:text-[#ff6b62] font-semibold text-sm transition-colors"
                    >
                      {s.link.label}
                      <ArrowRight size={14} />
                    </a>
                  )}
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* Avantages ATR */}
      <section className="py-16 md:py-24 bg-[#0A0A0A] border-t border-white/[0.06]">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-10 tracking-tight text-center">
            {cms.advantages_title_before || "Pourquoi choisir"} <span className="text-[#FF3B30]">{cms.advantages_title_highlight || "ATR"}</span>{cms.advantages_title_after || " ?"}
          </h2>

          <ul className="space-y-4">
            {advantages.map((a, i) => (
              <li
                key={i}
                data-testid={`reprog-advantage-${i}`}
                className="flex items-start gap-3 bg-black/40 border border-white/[0.06] rounded-lg p-4"
              >
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#FF3B30]/[0.12] flex items-center justify-center mt-0.5">
                  <Check size={14} className="text-[#FF3B30]" />
                </div>
                <span className="text-white/80 leading-relaxed">{a}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Prendre rendez-vous */}
      <section className="py-16 md:py-24 bg-[#121212] border-t border-white/[0.06]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 tracking-tight">{finalCta.title || "Prendre rendez-vous"}</h2>
          <p className="text-white/70 mb-10 max-w-2xl mx-auto">{finalCta.subtitle || "Devis gratuit par téléphone ou en atelier."}</p>

          <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto mb-8">
            <a
              href={`tel:${contact.phone}`}
              data-testid="reprog-contact-phone"
              className="bg-black/40 border border-white/[0.06] hover:border-[#FF3B30]/30 rounded-xl p-6 transition-colors flex items-center gap-4 text-left"
            >
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#FF3B30]/[0.12] flex items-center justify-center">
                <Phone size={20} className="text-[#FF3B30]" />
              </div>
              <div>
                <div className="text-white/50 text-xs uppercase tracking-wider mb-1">Téléphone</div>
                <div className="text-white font-bold text-lg">06 49 07 08 20</div>
              </div>
            </a>

            <a
              href={reviews.url || "https://maps.google.com/?cid=1681708394347082412"}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="reprog-contact-address"
              className="bg-black/40 border border-white/[0.06] hover:border-[#FF3B30]/30 rounded-xl p-6 transition-colors flex items-center gap-4 text-left"
            >
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#FF3B30]/[0.12] flex items-center justify-center">
                <MapPin size={20} className="text-[#FF3B30]" />
              </div>
              <div>
                <div className="text-white/50 text-xs uppercase tracking-wider mb-1">Adresse</div>
                <div className="text-white font-bold">{contact.street}</div>
                <div className="text-white/60 text-sm">{contact.postal_code} {contact.city}</div>
              </div>
            </a>
          </div>

          <a
            href="/#booking"
            data-testid="reprog-cta-book-bottom"
            className="inline-flex items-center gap-2 bg-[#FF3B30] hover:bg-[#D63025] text-white font-bold px-8 py-4 rounded-lg transition-colors uppercase tracking-wide text-sm"
          >
            {finalCta.button_label || "Réserver un créneau en ligne"}
            <ArrowRight size={16} />
          </a>
        </div>
      </section>

      <Footer businessHours={businessHours} content={homeContent} />
    </div>
  );
};

export default ReprogPage;
